import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink,
  Fade
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { GoogleLogin } from '@react-oauth/google';

// Ícones
import AltRouteIcon from '@mui/icons-material/AltRoute';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    verifyEmailPassword,
    clearLoginError,
    googleLogin,
    sendResetMail
  } = api;

  const auth = useSelector(state => state.auth);

  // Feature Flag: Login com Google ocultado programaticamente
  const ENABLE_GOOGLE_LOGIN = false;

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Validation State
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [feedbackError, setFeedbackError] = useState('');

  // Forgot Password Modal State
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // Email format regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Redirect on successful authentication
  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setIsLoading(false);
      const isSuperAdmin = Boolean(
        auth.profile.is_superadmin ||
        auth.profile.role === 'superadmin' ||
        auth.profile.usertype === 'superadmin'
      );
      const role = auth.profile.usertype;
      if (isSuperAdmin) {
        navigate('/tenants');
      } else if (role === 'driver') {
        navigate('/bookings');
      } else {
        navigate('/dashboard');
      }
    }
  }, [auth.profile, navigate]);

  // Auth Error Handling & Translation
  useEffect(() => {
    if (auth.error && auth.error.flag && auth.error.msg) {
      setIsLoading(false);
      const rawMsg = auth.error.msg.message || '';
      const rawCode = auth.error.msg.code || '';

      if (rawCode === 'auth/user-not-found' || rawMsg.includes('user-not-found')) {
        setFeedbackError('Usuário não encontrado. Verifique o e-mail informado.');
      } else if (rawCode === 'auth/wrong-password' || rawCode === 'auth/invalid-credential' || rawMsg.includes('wrong-password') || rawMsg.includes('invalid-credential')) {
        setFeedbackError('E-mail ou senha incorretos. Tente novamente.');
      } else if (rawCode === 'auth/user-disabled' || rawMsg.includes('user-disabled')) {
        setFeedbackError('Esta conta foi desativada. Entre em contato com o suporte.');
      } else if (rawCode === 'auth/too-many-requests' || rawMsg.includes('too-many-requests')) {
        setFeedbackError('Muitas tentativas incorretas. Aguarde alguns instantes antes de tentar novamente.');
      } else if (rawMsg === t('require_approval')) {
        setFeedbackError('Sua conta está pendente de aprovação pela administração.');
      } else {
        setFeedbackError('Falha no login. Verifique seus dados e tente novamente.');
      }
    }
  }, [auth.error, t]);

  // Field-level Validation
  const validateField = (field, value) => {
    let error = '';
    if (field === 'email') {
      if (!value.trim()) {
        error = 'O e-mail é obrigatório.';
      } else if (!emailRegex.test(value.trim())) {
        error = 'Informe um endereço de e-mail válido.';
      }
    }
    if (field === 'password') {
      if (!value) {
        error = 'A senha é obrigatória.';
      } else if (value.length < 6) {
        error = 'A senha deve conter no mínimo 6 caracteres.';
      }
    }
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const val = field === 'email' ? email : password;
    validateField(field, val);
  };

  const handleChangeEmail = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (feedbackError) setFeedbackError('');
    if (touched.email) validateField('email', val);
  };

  const handleChangePassword = (e) => {
    const val = e.target.value;
    setPassword(val);
    if (feedbackError) setFeedbackError('');
    if (touched.password) validateField('password', val);
  };

  const validateAll = () => {
    const isEmailValid = validateField('email', email);
    const isPasswordValid = validateField('password', password);
    setTouched({ email: true, password: true });
    return isEmailValid && isPasswordValid;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;

    setFeedbackError('');
    setIsLoading(true);
    dispatch(clearLoginError());

    try {
      await dispatch(verifyEmailPassword(email.trim(), password));
    } catch (err) {
      setIsLoading(false);
      setFeedbackError('Erro de conexão ao autenticar. Tente novamente.');
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    if (credentialResponse && credentialResponse.credential) {
      setIsLoading(true);
      dispatch(googleLogin(credentialResponse.credential, null));
    } else {
      setFeedbackError('Não foi possível autenticar com o Google.');
    }
  };

  // Forgot Password Actions
  const handleOpenForgot = () => {
    setForgotEmail(email || '');
    setForgotError('');
    setForgotSuccess(false);
    setForgotOpen(true);
  };

  const handleCloseForgot = () => {
    setForgotOpen(false);
    setForgotError('');
    setForgotSuccess(false);
  };

  const handleSendResetPassword = async () => {
    if (!forgotEmail.trim()) {
      setForgotError('Informe seu e-mail.');
      return;
    }
    if (!emailRegex.test(forgotEmail.trim())) {
      setForgotError('Informe um e-mail válido.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      await dispatch(sendResetMail(forgotEmail.trim()));
      setForgotSuccess(true);
    } catch (err) {
      setForgotError('Não foi possível enviar o e-mail de recuperação.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2.5,
        position: 'relative',
        background: 'radial-gradient(circle at 50% 20%, rgba(37, 99, 235, 0.15) 0%, transparent 70%), #0f172a'
      }}
    >
      <Helmet>
        <title>Login - Alpha Giro Controle de Frota</title>
      </Helmet>

      {/* Botão de Retorno à Homepage */}
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          color: '#94a3b8',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          bgcolor: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          px: 1.8,
          py: 0.8,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.08)' }
        }}
      >
        Voltar à página inicial
      </Button>

      {/* Card de Login Centralizado */}
      <Fade in={true} timeout={400}>
        <Card
          sx={{
            width: '100%',
            maxWidth: 440,
            bgcolor: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 40px rgba(37, 99, 235, 0.12)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 3.5, sm: 4.5 } }}>
            {/* Logo e Cabeçalho do Card */}
            <Box sx={{ textAlign: 'center', mb: 3.5 }}>
              <Avatar
                sx={{
                  bgcolor: '#2563eb',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  boxShadow: '0 8px 20px rgba(37, 99, 235, 0.35)',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <AltRouteIcon sx={{ color: '#ffffff', fontSize: 26 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
                Alpha Giro
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500, fontSize: '13.5px' }}>
                Acesse o controle de frota e rotas
              </Typography>
            </Box>

            {/* Alerta de Feedback de Erro */}
            {feedbackError && (
              <Alert
                severity="error"
                onClose={() => setFeedbackError('')}
                sx={{
                  mb: 3,
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  bgcolor: '#fee2e2',
                  color: '#991b1b',
                  border: '1px solid #fecaca'
                }}
              >
                {feedbackError}
              </Alert>
            )}

            {/* Formulário de Login */}
            <Box component="form" onSubmit={handleLoginSubmit} noValidate>
              {/* Campo E-mail */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.8, fontWeight: 700, color: '#334155', fontSize: '12px' }}>
                  E-mail corporativo
                </Typography>
                <TextField
                  fullWidth
                  id="email"
                  type="email"
                  placeholder="gestor@empresa.com.br"
                  value={email}
                  onChange={handleChangeEmail}
                  onBlur={() => handleBlur('email')}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon sx={{ fontSize: 19, color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                      bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                      '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                    }
                  }}
                  FormHelperTextProps={{
                    sx: { mx: 0.5, mt: 0.5, fontSize: '11.5px', fontWeight: 600 }
                  }}
                />
              </Box>

              {/* Campo Senha */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', fontSize: '12px' }}>
                    Senha
                  </Typography>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={handleOpenForgot}
                    sx={{
                      color: '#2563eb',
                      fontSize: '12px',
                      fontWeight: 600,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Esqueceu a senha?
                  </MuiLink>
                </Box>
                <TextField
                  fullWidth
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={handleChangePassword}
                  onBlur={() => handleBlur('password')}
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ fontSize: 19, color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: '#94a3b8' }}
                        >
                          {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                      bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                      '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                    }
                  }}
                  FormHelperTextProps={{
                    sx: { mx: 0.5, mt: 0.5, fontSize: '11.5px', fontWeight: 600 }
                  }}
                />
              </Box>

              {/* Botão Principal de Login */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                endIcon={!isLoading && <ArrowForwardIcon sx={{ fontSize: 16 }} />}
                sx={{
                  mt: 3,
                  py: 1.3,
                  bgcolor: '#2563eb',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '14.5px',
                  borderRadius: '10px',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={22} sx={{ color: '#ffffff' }} />
                ) : (
                  'Entrar no Sistema'
                )}
              </Button>
            </Box>

            {/* Login com Google (Ocultado programaticamente) */}
            {ENABLE_GOOGLE_LOGIN && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setFeedbackError('Falha no login com Google.')}
                  shape="rectangular"
                  theme="outline"
                  text="signin_with"
                  width="100%"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Modal de Recuperação de Senha */}
      <Dialog
        open={forgotOpen}
        onClose={handleCloseForgot}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            width: 440,
            maxWidth: '92vw'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '18px', color: '#0f172a' }}>
          Recuperar Senha
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748b', fontSize: '13.5px', mb: 2 }}>
            Digite o e-mail cadastrado na plataforma para receber o link de redefinição de senha.
          </DialogContentText>

          {forgotSuccess && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: '8px', fontSize: '13px' }}>
              E-mail de recuperação enviado com sucesso! Verifique sua caixa de entrada.
            </Alert>
          )}

          {forgotError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', fontSize: '13px' }}>
              {forgotError}
            </Alert>
          )}

          {!forgotSuccess && (
            <TextField
              autoFocus
              margin="dense"
              id="forgotEmail"
              label="E-mail cadastrado"
              type="email"
              fullWidth
              variant="outlined"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              disabled={forgotLoading}
              sx={{ mt: 1 }}
              InputProps={{
                sx: { borderRadius: '10px', fontSize: '14px' }
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseForgot}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
          >
            {forgotSuccess ? 'Fechar' : 'Cancelar'}
          </Button>
          {!forgotSuccess && (
            <Button
              onClick={handleSendResetPassword}
              variant="contained"
              disabled={forgotLoading}
              sx={{
                bgcolor: '#2563eb',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              {forgotLoading ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : 'Enviar Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}